export const APP_NAME = "Infillion Insights Dashboard";

export const TEXT = {
  appLayout: {
    darkModeToggle: "Dark Bloom",
    heading: APP_NAME,
    lightModeToggle: "Light Bloom",
  },
  auth: {
    login: {
      contactLinkLabel: "Contact your Infillion rep",
      emailInvalid: "Enter a valid email address",
      emailLabel: "Email",
      emailRequired: "Email is required",
      forgotPasswordLink: "Forgot your password?",
      invalidCredentials: "Email or password is incorrect",
      needAccessText: "Need access?",
      passwordLabel: "Password",
      passwordRequired: "Password is required",
      signInUnavailable:
        "Sign-in is unavailable right now. Try again in a moment.",
      submitButton: "Sign In",
      subtitle: "Sign in to view your campaign performance.",
      title: "Welcome back",
    },
    newPassword: {
      backToLoginLink: "Back to login",
      confirmPasswordLabel: "Confirm Password",
      confirmPasswordRequired: "Please confirm your password",
      linkInvalid:
        "This link is invalid or has expired. Ask your administrator to send a new invite.",
      linkInvalidTitle: "This link is not valid",
      newPasswordLabel: "New Password",
      newPasswordRequired: "Password is required",
      passwordMismatch: "Passwords do not match",
      passwordsMatch: "Passwords match",
      requirementMinLength: "At least 10 characters",
      requirementMixedCase: "A mix of upper case, lower case, and numbers",
      requirementNoRepeats: "No more than 2 identical characters in a row",
      requirementSpecialChar: "One special character (!@#%^&*)",
      setPasswordFailed: "Something went wrong. Try again in a moment.",
      strengthLabelFair: "Fair",
      strengthLabelStrong: "Strong",
      strengthLabelWeak: "Weak",
      submitButton: "Reset Password",
      subtitle: "Choose a strong password to secure your account.",
      title: "Set your new password",
    },
    promo: {
      badgeLabel: "Client Analytics Portal",
      copyright: "© 2026 Infillion. All rights reserved.",
      headlineBold: "Insights",
      headlineLight: "Unified",
      subtitle:
        "Cross-product performance data for your Infillion campaigns — in one place.",
      wordmark: "Infillion",
    },
    resetPassword: {
      backToLoginLink: "Back to login",
      confirmationMessage:
        "If an account exists for that email, a password reset link is on its way.",
      emailInvalid: "Enter a valid email address",
      emailLabel: "Email",
      emailRequired: "Email is required",
      enterpriseEmailNotice:
        "Your email's domain is part of an Enterprise Identity provider. To reset your password, please contact your security administrator.",
      ignoreLinkLabel: "Ignore this",
      noRequestText: "Didn't request a reset?",
      requestUnavailable:
        "We couldn't send that request. Check your connection and try again.",
      submitButton: "Send Reset Link",
      subtitle: "Enter your email and we'll send you a reset link.",
      title: "Forgot your password?",
    },
    sessionCheckFailed:
      "Something went wrong checking your session. Please refresh the page.",
  },
  campaignOverview: {
    arrival: {
      dwellDescription: "average time per visit",
      dwellEyebrow: "Avg Dwell Time",
      errorBody: "Could not retrieve foot traffic data.",
      errorHeading: "Failed to load Arrival data",
      noData: "No Arrival data available for this campaign and date range.",
      poiColAddress: "Address",
      poiColDwell: "Avg Dwell",
      poiColName: "POI",
      poiColUniqueVisits: "Unique Visits",
      poiColVisits: "Visits",
      poiCsvButton: "↓ CSV",
      poiEmptyMessage: "No rows match the current filter.",
      poiError: "Failed to load POI data",
      poiFilterPlaceholder: "Filter POIs...",
      poiNoData: "No POI data available for this campaign and date range.",
      poiRowsPerPage: "Rows per page",
      poiTitle: "POI performance",
      title: "Foot traffic",
      uniqueVisitsDescription: "unique visitors over the campaign period",
      uniqueVisitsEyebrow: "Unique Visits",
      visitsDescription: "projected visits attributed to this campaign",
      visitsEyebrow: "Projected Visits",
    },
    audienceSegment: {
      columnClicks: "Clicks",
      columnCtr: "CTR",
      columnImpressions: "Impressions",
      columnParentVendor: "Parent Vendor",
      columnSegmentName: "Segment Name",
      columnVcr: "VCR",
      columnVendor: "Vendor",
      csvButton: "↓ CSV",
      emptyMessage: "No rows match the current filter.",
      error: "Failed to load audience segment data.",
      filterPlaceholder: "Filter by segment or vendor…",
      noData:
        "No audience segment data for the selected campaigns and date range.",
      rowsPerPage: "Rows per page",
      sectionLabel: "Who Saw It",
      subtitle: "Ranked by click-through rate",
      title: "Audience segments",
    },
    badgeActive: "Active",
    badgeEnded: "Ended",
    badgeUpcoming: "Upcoming",
    channelBreakdown: {
      error: "Failed to load channel data",
      noData: "No data for this date range.",
      sectionLabel: "Where It Ran",
      title: "Impressions by channel",
      typeDonut: "Donut",
      typeHorizontalBar: "Horizontal",
    },
    chart: {
      impressionsSeriesLabel: "Impressions",
      spendSeriesLabel: "Total Spend",
      title: "Performance over time",
      typeArea: "Area",
      typeBar: "Bar",
      typeLine: "Line",
    },
    creativePerformance: {
      emptyMessage:
        "No creative data for the selected campaigns and date range.",
      error: "Failed to load creative performance data.",
      impressionsByCreativeTitle: "Impressions by creative",
      impressionsBySizeTitle: "Impressions by creative size",
      legendCtr: "CTR",
      legendImpressions: "Impressions",
      showAll: "Show all",
      showTop10: "Top 10",
      sizeNote: "0×0 and 1×1 are native or video",
      tooltipId: "ID",
      tooltipName: "Name",
    },
    deviceBrowser: {
      browser: "Browser",
      dataFilters: "Data filters",
      deviceType: "Device Type",
      error: "Failed to load device and browser data.",
      noData: "No device data for the selected campaigns and date range.",
      noMatchesFound: "No matches found",
      osType: "OS Type",
      panelSubtitle: "Impressions and CTR by browser",
      panelTitle: "Device and browser performance",
      searchBrowsersPlaceholder: "Search browsers...",
      searchDevicesPlaceholder: "Search devices...",
      searchOsTypesPlaceholder: "Search OS types...",
      sectionLabel: "How It Reached Them",
    },
    emptyState: {
      agency: {
        description:
          "Choose one or more Advertisers and Campaigns using the selectors above to view your insights.",
        step1Description: "Narrow to the brands or clients within your agency.",
        step1Title: "Choose one or more Advertisers",
        step2Description:
          "Pick one or more campaigns — metrics are combined across your selection.",
        step2Title: "Select Campaigns",
      },
      heading: "Select campaigns to get started",
      infillion: {
        description:
          "Choose one or more Agencies, Advertisers and Campaigns using the selectors above to view your insights.",
        step1Description:
          "Select one or more agencies whose campaigns you want to view.",
        step1Title: "Pick one or more Agencies",
        step2Description:
          "Narrow to the brands or clients within your selected agencies.",
        step2Title: "Choose one or more Advertisers",
        step3Description:
          "Pick one or more campaigns — metrics are combined across your selection.",
        step3Title: "Select Campaigns",
      },
    },
    geo: {
      cardTopCtr: "Top Market by CTR",
      cardTopImpressions: "Top Market by Impressions",
      csvButton: "↓ CSV",
      emptyMessage: "No regional data available",
      error: "Failed to load geographic data",
      filterPlaceholder: "Filter regions...",
      gridTitle: "Regions",
      keyFewer: "Fewer",
      keyMore: "More impressions",
      noData: "No geographic data for the selected campaigns and date range.",
      title: "Impressions by state",
      topCitiesTitle: "Top markets",
      viewGrid: "Grid",
      viewMap: "Map",
    },
    kpi: {
      clicks: "Total Clicks",
      conversions: "Total Conversions",
      cpa: "Blended CPA",
      ctr: "Blended CTR",
      error: "Failed to load performance data",
      impressions: "Total Impressions",
      noData: "No data for this date range.",
      notApplicable: "N/A",
      projectedVisits: "Projected Visits",
      totalSpend: "Total Spend",
      vcr: "Video Completion Rate",
    },
    labels: {
      other: "Other",
      unknown: "Unknown",
    },
    multipleCampaignsTitle: "Campaigns Selected",
    noCampaignSelected: "Select a campaign to view its overview",
    performanceTable: {
      columnAdvertiser: "Advertiser",
      columnCampaign: "Campaign",
      columnClicks: "Clicks",
      columnConversions: "Conversions",
      columnCpa: "CPA",
      columnCtr: "CTR",
      columnEcpm: "eCPM",
      columnImpressions: "Impressions",
      columnStrategyGroup: "Strategy Group",
      columnVcr: "VCR",
      columnVideoCompletes: "Video Completes",
      csvButton: "↓ CSV",
      emptyMessage: "No rows match the current filter.",
      error: "Failed to load performance data",
      filterPlaceholder: "Filter rows...",
      rowsPerPage: "Rows per page",
      sectionLabel: "Detailed Reporting",
      title: "Performance table",
    },
    productTabs: {
      mmDisplay: "MM/NeXt Display",
    },
    switcher: {
      chartType: "Chart type",
      rowsShown: "Rows shown",
      view: "View",
    },
    topPerformers: {
      creativePanel: {
        subtitleAll: "Impressions and CTR for all creatives",
        subtitleTop: "Impressions and CTR for the top 10 creatives",
        subtitleTopWithLeader:
          "Impressions and CTR for the top 10 creatives and the top CTR creative",
        title: "CTR by creative",
      },
      sectionLabel: "Top performers",
      showAll: "Show all",
      showTop10: "Top 10",
      showingDetail: "Showing detail",
      sizeBubble: {
        blended: "Blended",
        bubbleSizeKey: "Bubble size = clicks",
        clicks: "Clicks",
        ctr: "CTR",
        impressions: "Impressions",
        oneSizeKey: "One creative size",
        subtitle: "Each size by volume and click-through rate",
        title: "Impressions and clicks by creative size",
        xAxisTitle: "Impressions (log scale)",
        yAxisTitle: "CTR",
      },
      tileBrowser: "Top browser by CTR",
      tileCreative: "Top creative by CTR",
      tileSegment: "Top audience segment by CTR",
      tileSize: "Top creative size by CTR",
      viewDetail: "View detail",
    },
    videoQuartiles: {
      error: "Failed to load video quartile data.",
      noData: "No video data for the selected campaigns and date range.",
      stages: {
        complete: "Complete",
        firstQuartile: "25%",
        midpoint: "50%",
        start: "Start",
        thirdQuartile: "75%",
      },
      title: "Video quartiles",
      typeCurve: "Curve",
      typeFunnel: "Funnel",
      typeHorizontalBar: "Horizontal",
      vcr: "VCR",
    },
    viewability: {
      centerCaption: "in view",
      errorBody: "Could not retrieve data from the Reporting API.",
      errorHeading: "Failed to load viewability",
      noData: "No viewability data available for this selection.",
      rate100Description: "of impressions were fully in view.",
      rate100UnavailableNote:
        "Fully in-view is not available for this placement.",
      rate50Note: "In-view rate at the 50% threshold.",
      title: "Viewability",
    },
  },
  datePresetTabs: {
    custom: "Custom",
    day: "Day",
    month: "Month",
    week: "Week",
  },
  dateRangePicker: {
    applyButton: "Apply Range",
    cancelButton: "Cancel",
    dataThroughYesterday: "Data through yesterday (T-1)",
    daysCountLabel: "{0} days",
    endDateLabel: "End Date",
    lastFourteenDays: "Last 14 days",
    lastMonth: "Last month",
    lastNinetyDays: "Last 90 days",
    lastQuarter: "Last quarter",
    lastSevenDays: "Last 7 days",
    lastThirtyDays: "Last 30 days",
    quickSelectLabel: "Quick Select",
    selectableWindowNote:
      "Future dates unavailable · T-1 data · Up to 1 year back",
    startDateLabel: "Start Date",
    thisMonth: "This month",
    thisQuarter: "This quarter",
    yesterday: "Yesterday",
  },
  hierarchyBreadcrumb: {
    advertiserEmpty: "No advertisers available",
    advertiserEmptyHint: "Try a different agency",
    advertiserError: "Failed to load advertisers",
    advertiserPlaceholder: "Advertiser",
    advertiserSelectedItemsLabel: "{0} Advertisers selected",
    agencyEmpty: "No agencies available",
    agencyError: "Failed to load agencies",
    agencyPlaceholder: "Agency",
    agencySelectedItemsLabel: "{0} Agencies selected",
    badgeActive: "Active",
    badgeEnded: "Ended",
    badgeUpcoming: "Upcoming",
    campaignEmpty: "No campaigns available",
    campaignEmptyHint: "Try a different agency or advertiser",
    campaignError: "Failed to load campaigns",
    campaignPlaceholder: "Campaign",
    campaignSelectedItemsLabel: "{0} Campaigns selected",
    campaignStatusAll: "All",
    noMatchesFound: "No matches found",
    searchAdvertisersPlaceholder: "Search advertisers...",
    searchAgenciesPlaceholder: "Search agencies...",
    searchCampaignsPlaceholder: "Search campaigns...",
  },
  profileMenu: {
    signOutButton: "Sign out",
  },
  sidebar: {
    adminSection: "Admin",
    collapse: "Collapse sidebar",
    expand: "Expand sidebar",
    logoLabel: "Infillion Analytics Portal",
    portalLabel: "Analytics Portal",
    unifiedInsights: "Unified Insights",
    userManagement: "User Management",
  },
  topBar: {
    exportButton: "Export",
    exportingButton: "Exporting…",
  },
  userManagement: {
    agencyLookupFailed:
      "Could not load this user's current agency. Saving now would change it.",
    agencyRequired: "Select an agency",
    agencySearchPlaceholder: "Search agencies...",
    allRolesLabel: "All Roles",
    cancelButton: "Cancel",
    columnAgency: "Agency",
    columnId: "User ID",
    columnLastLogin: "Last Login",
    columnRole: "Role",
    columnUser: "User",
    editButton: "Edit",
    emailInvalid: "Enter a valid email address (128 characters or fewer)",
    emailLabel: "Email",
    emailRequired: "Email is required",
    firstNameLabel: "First Name",
    firstNameRequired: "First name is required",
    firstNameTooLong: "First name must be 32 characters or fewer",
    inviteNotSent:
      "The account was created, but the invite email did not send. Use Resend Invite.",
    inviteResent: "Invite sent.",
    inviteUserButton: "Invite User",
    lastNameLabel: "Last Name",
    lastNameRequired: "Last name is required",
    lastNameTooLong: "Last name must be 32 characters or fewer",
    loadFailed: "Could not load users. Try again.",
    loadingUsers: "Loading users…",
    modalAddTitle: "Invite User",
    modalEditTitle: "Edit User",
    neverLoggedIn: "Never",
    noUsersMatchFilters: "No users match these filters",
    noUsersYet: "No users yet — invite someone to get started",
    pageSubtitle: "Manage portal access across all agencies",
    pageTitle: "User Management",
    resendInviteButton: "Resend Invite",
    roleAdmin: "Admin",
    roleManager: "Manager",
    roleReporter: "Reporter",
    saveChangesButton: "Save Changes",
    saveFailed: "Could not save. Try again.",
    searchPlaceholder: "Search by name, email or user ID...",
    sendInviteButton: "Send Invite",
    showingCountLabel: "Showing {0} of {1} users",
    usersCountLabel: "{0} users",
    youSuffix: "(you)",
  },
} as const;
